import { savableSpaces } from "../model/html-text";
import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber, map } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Ticket } from "../model/ticket";
import { FilterService } from "./filter.service";
import { NavigationHelper } from "./navigation.helper";
import { FilterGroup } from "../model/filters/filterGroup";
import { MultiActionService } from "./multi-action.service";
import { Injector } from "@angular/core";
@Injectable({
  providedIn: "root",
})
export class TicketService {
  private injector: Injector;
  private apiUrl: string;
  private apiFilterUrl: string;
  private byIdUrl: string;
  private pageUrl: string;
  private withPartsUrl: string;
  public _currentTicket: BehaviorSubject<Ticket[]>;
  private resultIds: BehaviorSubject<number[]>;
  public selectedTicket: BehaviorSubject<Ticket | null>;
  private _formState = new BehaviorSubject<Partial<Ticket>>({});
  public currentPage: BehaviorSubject<number>;
  public pageSize: number;
  private selectedMultiActionId: BehaviorSubject<number>;
  private selectedMultiActionUrl: BehaviorSubject<string>;
  public selectedTickets = new BehaviorSubject<Ticket[]>([]);
  private resultIdsByBranch = new BehaviorSubject<Map<number, number[]>>(new Map());
  private apiFilterByBranchUrl: string;
  public selectTickets = this.selectedTickets.asObservable();
  private _multiActionService: MultiActionService;
  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService,
    public filterService: FilterService,
    private navigation: NavigationHelper
  ) {
    this.selectedMultiActionUrl = new BehaviorSubject<string>(
      "/tickets/1/multi-process/action/1"
    );
    this._currentTicket = new BehaviorSubject<Ticket[]>([]);
    this.resultIds = new BehaviorSubject<number[]>([]);
    this.currentPage = new BehaviorSubject<number>(1);
    this.selectedTicket = new BehaviorSubject<Ticket | null>(null);
    this.pageSize = 10;
    this.apiUrl = `${env.apiBaseUrl}ticket/`;
    this.byIdUrl = `${env.apiBaseUrl}ticket/by-id/`;
    this.withPartsUrl = `${env.apiBaseUrl}ticket/with-parts/`;
    this.apiFilterUrl = `${env.apiBaseUrl}ticket/filter`;
    this.apiFilterByBranchUrl = `${env.apiBaseUrl}ticket/filter/by-branch`;
    this.pageUrl = `${env.apiBaseUrl}ticket/by-page/`;
    this.selectedMultiActionId = new BehaviorSubject<number>(0);
    this.navigation.topic.subscribe((topic) => {
      this.applyAllByNavigation();
    });

    this.resultIds.subscribe((ids) => {
      const token = this.auth.token;

      if (ids.length === 0) {
        this._currentTicket.next([]);
      } else if (ids.length <= this.pageSize) {
        this.page(token, ids).subscribe((messages) => { });
      } else {
        const part = this.pageOfIds;
        this.page(token, part).subscribe((messages) => { });
      }
    });
  }

  public applyAllByNavigation() {
    const nav = this.navigation.current;
    if (nav.action === "result") {
      if (nav.fragmentTopic === "page") {
        const pageNumber = Number(nav.fragmentId);

        this.currentPage.next(pageNumber);

        const token = this.auth.token;
        var idSize = this.currentResultIds.length;

        if (idSize > 0) {
          const page =
            idSize > this.pageSize ? this.pageOfIds : this.currentResultIds;

          this.page(token, page).subscribe((messages) => {
            if (messages.hasAnyMessages) {
            }
          });
        } else {
        }
      }
    }
  }

  public search(token: string): Observable<Messages> {
    const filter = this.filterService.filterCurrent;
    const group =
      "match.all" === filter.code || "match.any" === filter.code
        ? (filter as FilterGroup)
        : null;

    if (group && (!group.filters || group.filters.length === 0)) {
      this.filterService.setStandardFilter();
    }

    const url = `${this.apiFilterUrl}`;

    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(url, this.filterService.filterCurrent, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveResultIds(response, observer),
          (error) => this.receiveResultIds(error, observer)
        );
    });
  }

  private receiveResultIds(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<any>)?.body;

      if (!data) {
        observer.next(Messages.from({ filter: ["filter.unknown"] }));
        return;
      }

      this.resultIds.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(Messages.from({ account: ["login.not.authorized"] }));
    } else if (response.status === 400) {
      observer.next(Messages.from({ filter: ["filter.wrong.data"] }));
    } else if (response.status === 409 || response.status === 500) {
      observer.next(Messages.from((response as HttpErrorResponse)?.error));
    } else {
      observer.next(
        Messages.from({
          filter: [
            "filter.technical.error " + response.status,
            (response as HttpErrorResponse)?.error as string,
          ],
        })
      );
    }
  }
  public page(token: string, ids: number[]): Observable<Messages> {
    if (!ids) {
      ids = this.pageOfIds;
    }
    const page = ids.join("-");

    const url = this.pageUrl + page;

    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receiveTickets(response, observer);
          },
          (error) => {
            this.receiveTickets(error, observer);
          }
        );
    });
  }

  public createTicket(token: string, ticket: Ticket): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        // stored with ordinary spaces between words, not the editor's non-breaking ones (#173)
        .post<any>(this.apiUrl, { ...ticket, description: savableSpaces(ticket.description) }, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receiveTicket(response, observer);
          },
          (error) => {
            this.receiveTicket(error, observer);
          }
        );
    });
  }

  public updateTicket(token: string, ticket: Ticket): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${ticket.id}`;

    return new Observable<Messages>((observer) => {
      if (!ticket.id) {
        observer.next(Messages.from({ ticket: ["ticket.id.required"] }));
        return;
      }

      this.http
        .put<any>(url, { ...ticket, description: savableSpaces(ticket.description) }, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTicket(response, observer),
          (error) => this.receiveTicket(error, observer)
        );
    });
  }

  public deleteTicket(token: string, ticketId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${ticketId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTicket(response, observer),
          (error) => this.receiveTicket(error, observer)
        );
    });
  }

  public getTicketById(token: string, ticketId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${ticketId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTicket(response, observer),
          (error) => this.receiveTicket(error, observer)
        );
    });
  }

  private receiveTickets(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Ticket[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ tickets: ["ticket.unknown"] }));
        return;
      }

      this._currentTicket.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "tickets",
          "tickets.error"
        )
      );
    }
  }

  private receiveTicket(
    response: HttpResponseBase,
    observer: Subscriber<Messages>,
    ticket?: Ticket
  ) {
    if (response.status === 204) {
      observer.next(Messages.EMPTY);
      return;
    }

    if (response.status === 200) {
      const data = (response as HttpResponse<Ticket>)?.body;
      if (!data) {
        observer.next(Messages.from({ ticket: ["ticket.unknown"] }));
        return;
      }

  if (ticket) {
    Object.assign(ticket, data);
    if (data.parts) {
      ticket.parts = data.parts;
    }
    this.selectedTicket.next(ticket);
  } else {
    this.selectedTicket.next(data);
  }

  observer.next(Messages.EMPTY);

    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "ticket",
          "ticket.load.error."
        )
      );
    } else if (response.status === 404) {
      // the server answers 404 with an empty body, so there is nothing to
      // unpack and the fallback is the whole message. Without this the reply
      // fell through to "an unexpected error", which it is not: the ticket is
      // gone, and that is worth saying plainly.
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "ticket",
          "ticket.not.found"
        )
      );
    } else if (response.status === 403) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "ticket",
          "ticket.not.allowed"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public getTicketWithParts(
    token: string,
    ticketId: number,
    ticket?: Ticket
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.withPartsUrl}${ticketId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTicket(response, observer, ticket),
          (error) => this.receiveTicket(error, observer, ticket)
        );
    });
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();
    if (segments && segments.length > 0) {
      const ticketId = segments[1];
      const token = this.auth.token;

      if (this.isNumeric(ticketId)) {
        this.getTicketWithParts(token, Number(ticketId), undefined).subscribe();
      } else {
      }
    } else {
      this.select(Ticket.EMPTY);
    }
  }

  private isNumeric(n: any): boolean {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

public select(ticket: Ticket, forceReload: boolean = false) {
  if (ticket && ticket.id !== null) {
    const token = this.auth.token;

    if (!ticket.parts || forceReload) {
      this.getTicketWithParts(token, ticket.id, ticket).subscribe({
        next: (messages) => {
          this.selectedTicket.next(ticket);
        },
      });
    } else {
  
      this.selectedTicket.next(ticket);
    }
  } else {
    this.selectedTicket.next(ticket);
  }
}


  public get selectedTicketObservable(): Observable<Ticket | null> {
    return this.selectedTicket.asObservable();
  }

  public get tiketObservable(): Observable<Ticket[]> {
    return this._currentTicket;
  }

  public get ticketData(): Ticket[] {
    return this._currentTicket.value;
  }

  public get currentTicket(): Ticket {
    const value = this.selectedTicket.value;
    return value || Ticket.EMPTY;
  }
  get formState(): Observable<Partial<Ticket>> {
    return this._formState.asObservable();
  }

  setFormState(state: Partial<Ticket>) {
    localStorage.setItem("ticketFormBackup", JSON.stringify(state));
    this._formState.next(state);
  }

  restoreFormState(emit: boolean = true): Partial<Ticket> {
    const savedState = localStorage.getItem("ticketFormBackup");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (emit) {
          this._formState.next(parsedState);
        }
        return parsedState;
      } catch (e) { }
    }

    return {};
  }

  clearFormStateBackup() {
    localStorage.removeItem("ticketFormBackup");
  }

  get currentselectedMultiActionId(): number {
    return this.selectedMultiActionId.value;
  }

  get selectedMultiActionNumber(): Observable<number> {
    return this.selectedMultiActionId;
  }

  get currentselectedMultiActionUrl(): string {
    return this.selectedMultiActionUrl.value;
  }

  get selectedMultiActionUri(): Observable<string> {
    return this.selectedMultiActionUrl;
  }

  selectMultiActionId(id: number) {
    this.selectedMultiActionId.next(id);

    const url = "/tickets/1/multi-process/action/" + id;

    this.selectedMultiActionUrl.next(url);
  }

  get currentResultIds(): number[] {
    return this.resultIds.value;
  }
  get currentResults(): Observable<number[]> {
    return this.resultIds.asObservable();
  }

  get currentPageNumber(): number {
    return this.currentPage.value;
  }

  get pageNumber(): Observable<number> {
    return this.currentPage.asObservable();
  }
  get numberOfPages(): number {
    return this.currentResultIds.length / this.pageSize + 1;
  }

  get pageNumbers(): Array<number> {
    const pageNumebers: Array<number> = [];
    if (this.numberOfPages < 11) {
      for (let i = 1; i <= this.numberOfPages; i++) {
        pageNumebers.push(i);
      }
    } else {
      const p = this.numberOfPages;
      const c = this.currentPage.value;
      //  1 2 3 . 25 26 27 28 29 . 98 99 100

      pageNumebers.push(1, 2, 3);
      if (c > 4) {
        pageNumebers.push(-1);
      }

      for (let i = c - 2; i <= c + 2; i++) {
        if (i > 3 && i < p - 2) {
          pageNumebers.push(i);
        }
      }

      if (c > p - 4) {
        pageNumebers.push(-1);
      }
      pageNumebers.push(p - 2, p - 1, p);
    }
    return pageNumebers;
  }
get startRow(): number {
  const current = this.currentPage.value < 1 ? 1 : this.currentPage.value;
  return (current - 1) * this.pageSize + 1;
}

get endRow(): number {
  const current = this.currentPage.value < 1 ? 1 : this.currentPage.value;
  const end = current * this.pageSize;
  return Math.min(end, this.currentResultIds.length);
}

  get pageOfIds(): Array<number> {
    const end = this.endRow;
    const start = end - this.pageSize;
    const size = this.currentResultIds.length;

    if (start > size) {
      return [];
    }
    if (end > size) {
      return this.currentResultIds.slice(start, size);
    } else {
      return this.currentResultIds.slice(start, end);
    }
  }


  get hasPagination(): boolean {
    return this.currentResultIds.length > this.pageSize;
  }

  public toggleTicketSelection(ticket: Ticket): void {
    const current = this.selectedTickets.getValue();
    const index = current.findIndex((t) => t.id === ticket.id);

    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(ticket);
    }
    this.selectedTickets.next([...current]);
  }

  public isTicketSelected(ticket: Ticket): boolean {
    return this.selectedTickets.getValue().some((t) => t.id === ticket.id);
  }
  private get multiActionService(): MultiActionService {
    if (!this._multiActionService) {
      this._multiActionService = this.injector.get(MultiActionService);
    }
    return this._multiActionService;
  }

 public getPage(pageNumber: number, token: string): Observable<Messages> {
  this.currentPage.next(pageNumber);
  const start = (pageNumber - 1) * this.pageSize;
  const end = start + this.pageSize;
  const idsForPage = this.currentResultIds.slice(start, end);

  return new Observable<Messages>((observer) => {
    this.page(token, idsForPage).subscribe({
      next: (messages) => {
        observer.next(messages);
        observer.complete();
      },
      error: (err) => observer.error(err),
    });
  });
}

  public searchByBranch(token: string): Observable<Messages> {
    const filter = this.filterService.filterCurrent;
    const group =
      "match.all" === filter.code || "match.any" === filter.code
        ? (filter as FilterGroup)
        : null;

    if (group && (!group.filters || group.filters.length === 0)) {
      this.filterService.setStandardFilter();
    }

    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(this.apiFilterByBranchUrl, this.filterService.filterCurrent, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => {
            if (response.status === 200) {
              const data = response.body;
              if (!data) {
                observer.next(Messages.from({ filter: ["filter.unknown"] }));
                return;
              }
              const branchMap = new Map<number, number[]>();
              for (const key of Object.keys(data)) {
                branchMap.set(Number(key), data[key]);
              }
              this.resultIdsByBranch.next(branchMap);
              observer.next(Messages.EMPTY);
            } else if (response.status === 401) {
              observer.next(Messages.from({ account: ["login.not.authorized"] }));
            } else {
              observer.next(Messages.from({ filter: ["filter.technical.error"] }));
            }
          },
          (error: HttpErrorResponse) => {
            if (error.status === 401) {
              observer.next(Messages.from({ account: ["login.not.authorized"] }));
            } else if (error.status === 400) {
              observer.next(Messages.from({ filter: ["filter.wrong.data"] }));
            } else {
              observer.next(Messages.from({ filter: ["filter.technical.error " + error.status] }));
            }
          }
        );
    });
  }

  get resultsByBranch(): Observable<Map<number, number[]>> {
    return this.resultIdsByBranch.asObservable();
  }

  get currentResultIdsByBranch(): Map<number, number[]> {
    return this.resultIdsByBranch.value;
  }

  public fetchTickets(token: string, ids: number[]): Observable<Ticket[]> {
    const url = this.pageUrl + ids.join("-");
    return this.http
      .get<Ticket[]>(url, {
        headers: { Authorization: "Bearer " + token },
      });
  }

}
